/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: ApiDocs
 * File     : ApiDocs.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the ApiDocs view and coordinating data fetching.
 *   Exports: ApiDocs
 *   
 *
 * Dependencies:
 *   - @mui/material
 *   - react
 *
 * Usage:
 *   Use in Router: <Route path="/apidocs" element={<ApiDocs />} />
 * -----------------------------------------------------------------------------
 */
import React from 'react';
import { Box, Typography, Divider, Card, CardContent, Chip, Stack } from '@mui/material';
import { API_BASE_URL } from '../services/api';

const Code = ({ children }) => (
  <Box
    component="pre"
    sx={(theme) => ({
      whiteSpace: 'pre-wrap',
      p: 1.5,
      borderRadius: 1,
      overflow: 'auto',
      fontFamily: 'monospace',
      bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
      color: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.900',
      border: '1px solid',
      borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.300',
    })}
  >
    <code>{children}</code>
  </Box>
);

const Badge = ({ label }) => (
  <Chip label={label} size="small" sx={{ mr: 1 }} />
);

const CardSection = ({ title, subtitle, badges = [], children }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        <Box>
          {badges.map((b) => <Badge key={b} label={b} />)}
        </Box>
      </Stack>
      <Box sx={{ mt: 2 }}>{children}</Box>
    </CardContent>
  </Card>
);

const ApiDocs = () => {
  const base = API_BASE_URL;
  return (
    <Box>
      <Typography variant="h4" gutterBottom>API Documentation</Typography>
      <Typography variant="body1" sx={{ opacity: 0.9 }}>
        All authenticated endpoints require <strong>Authorization: Bearer &lt;token&gt;</strong>.
      </Typography>

      <CardSection title="Authentication" subtitle="Register, login, Discord OAuth, and fetch current user" badges={["Auth","Public"]}>
        <Typography variant="subtitle1">POST {base}/auth/register</Typography>
        <Code>{`Body: { username, email, password }
Behavior: If email is enabled, sends a 6-digit code (expires in 5 minutes) and returns 202.
Response: { token, user } on immediate creation; otherwise { message } and wait for verify.
Notes:
- Usernames and emails are normalized to lowercase on the server.
`}</Code>
        <Typography variant="subtitle1">POST {base}/auth/verify-email</Typography>
        <Code>{`Body: { email, code }
Response: { token, user }
Notes:
- Code expires in 5 minutes; 5 invalid attempts will lock the pending signup.`}</Code>
        <Typography variant="subtitle1">POST {base}/auth/resend-code</Typography>
        <Code>{`Body: { email }
Response: { message }
Notes:
- Max 5 resends; each resend refreshes the 5-minute code expiry.`}</Code>
        <Typography variant="subtitle1">POST {base}/auth/login</Typography>
        <Code>{`Body: { username, password }
Response: { token, user }
Notes:
- Username is matched case-insensitively; server lowercases before lookup.
`}</Code>
        <Typography variant="subtitle1">GET {base}/auth/me</Typography>
        <Code>Returns current user.</Code>
        <Typography variant="subtitle1">PUT {base}/auth/me</Typography>
        <Code>{`Body: { email?, password?, displayName? }
Response: { user }
Notes:
- Either field is optional. Password must be at least 6 characters.
- Email must be valid format.
- displayName is optional and used across UI labels instead of username when present.
- Requires Bearer token.
`}</Code>
        <Typography variant="subtitle1">GET {base}/auth/discord/login</Typography>
        <Code>{`Redirects to Discord OAuth.
Notes:
- Requires environment variables DISCORD_CLIENT_ID/SECRET on backend.
- Frontend captures #token from callback and stores it automatically.
`}</Code>
        <Typography variant="subtitle1">GET {base}/auth/discord/callback</Typography>
        <Code>{`Used by Discord to redirect back with an authorization code. The backend exchanges the code for a token and redirects to the frontend with #token.`}</Code>
      </CardSection>

      <CardSection title="Polls" subtitle="List, read, create, update, delete" badges={["Public","Auth","Admin/Owner"]}>
        <Typography variant="subtitle1">GET {base}/polls</Typography>
        <Code>{`Query: { active?: true|false, page?: number, limit?: number, clubId?: number }
Response: { polls: Poll[], totalPages, currentPage, total }`}</Code>
        <Typography variant="subtitle1">GET {base}/polls/:id</Typography>
        <Code>Returns a single poll with movies, creator, winner, club.</Code>
        <Typography variant="subtitle1">POST {base}/polls (Auth)</Typography>
        <Code>{`Body: { title, description?, endDate (ISO), movies: [{ Title, Year, imdbID, Poster }], clubId?, sendDiscordAlert? }
Response: Poll
Notes:
- All authenticated users can create public polls (clubId=null)
- Only users with active subscriptions can create club polls (clubId provided)
- If sendDiscordAlert=true and club has a Discord webhook configured, an alert is posted.
`}</Code>
        <Typography variant="subtitle1">POST {base}/polls/random (Admin/Owner)</Typography>
        <Code>{`Body: { count (2-10), genre?, actor?, leadOnly?: boolean, director?, clubId?, title?, description?, endDate? }
Notes:
- Uses Plex libraries; optional genre filter via Plex metadata.
- Actor/leadOnly/director filters use Plex metadata (best‑effort).
- Public polls synthesize club as { name: "Public" } for UI.
Response: Poll`}</Code>
        <Typography variant="subtitle1">PUT {base}/polls/:id (Admin/Owner)</Typography>
        <Typography variant="subtitle1">DELETE {base}/polls/:id (Admin/Owner; Admins only if active)</Typography>
      </CardSection>

      <CardSection title="Votes" subtitle="Cast and view results" badges={["Public","Auth"]}>
        <Typography variant="subtitle1">POST {base}/votes</Typography>
        <Code>{`Body: { pollId (uuid), movieId (int) }
Response: { message, voteId }
Notes:
- One vote per IP per poll (server enforced).
- If logged in, vote is associated to your user for history.
`}</Code>
        <Typography variant="subtitle1">GET {base}/votes/results/:pollId</Typography>
        <Code>Returns aggregate results and persists winner if poll ended.</Code>
        <Typography variant="subtitle1">GET {base}/votes/me (Auth)</Typography>
        <Code>{`Returns your past votes with poll and movie details.
Response: { votes: [{ id, pollId, movieId, createdAt, poll, movie }] }`}</Code>
      </CardSection>

      <CardSection title="Movies" subtitle="OMDb/Watchmode powered" badges={["Public"]}>
        <Typography variant="subtitle1">GET {base}/movies/:imdbID</Typography>
        <Code>Returns OMDb details (plot, runtime, genres, cast, director, ratings).</Code>
        <Typography variant="subtitle1">GET {base}/movies/by-title?title=...&year=...</Typography>
        <Code>Finds a title by name/year and returns OMDb details (fallback when IMDb ID is unknown).</Code>
        <Typography variant="subtitle1">GET {base}/movies/watchmode/:imdbID</Typography>
        <Code>Returns streaming sources (Watchmode) for an IMDb ID. If you only have title/year, call GET {base}/movies/watchmode?title=...&year=...&region=US.</Code>
      </CardSection>

      <CardSection title="Ratings" subtitle="Rate winners after runtime" badges={["Auth"]}>
        <Typography variant="subtitle1">POST {base}/ratings (Auth)</Typography>
        <Code>{`Body: { pollId, movieId, rating (0.5-5 in 0.5 steps) }
Validation: Only after poll end + runtime of winner; must have voted on the poll; one-time (immutable).
Response: { message }`}</Code>
        <Typography variant="subtitle1">GET {base}/ratings/opportunities (Auth)</Typography>
        <Code>{`Returns rating opportunities, including upcoming ones with eligibleAt and countdowns.
Logic:
- Poll must be ended and winner recorded (backfilled if needed).
- eligibleAt = end + winner.runtimeMinutes.
- Excludes polls you've already rated.
- Includes polls you voted on and ended polls from clubs you belong to (configurable).
`}</Code>
      </CardSection>

      <CardSection title="Clubs" subtitle="Create private groups and run polls" badges={["Auth","Owner"]}>
        <Typography variant="subtitle1">GET {base}/clubs (Auth)</Typography>
        <Code>Owner sees all clubs; others see clubs they belong to.</Code>
        <Typography variant="subtitle1">POST {base}/clubs (Auth; Host plan or Owner)</Typography>
        <Code>{`Body: { name }
Rules: English letters/numbers/spaces only; banned words rejected; normalized and trimmed.`}</Code>
        <Typography variant="subtitle1">GET {base}/clubs/:id/invite (Auth; Owner/Admin/Club owner)</Typography>
        <Typography variant="subtitle1">POST {base}/clubs/join (Auth)</Typography>
        <Code>{`Body: { inviteCode }`}</Code>
        <Typography variant="subtitle1">POST {base}/clubs/:id/leave (Auth)</Typography>
        <Typography variant="subtitle1">GET {base}/clubs/:id (Auth)</Typography>
        <Code>{`Response: { id, name, ownerUserId, owner: { id, username, displayName } }
Notes: Get club details. Only club members can access.`}</Code>
        <Typography variant="subtitle1">GET {base}/clubs/:id/polls (Auth)</Typography>
        <Code>Owner can view any; members can view their club's polls.</Code>
        <Typography variant="subtitle1">DELETE {base}/clubs/:id (Auth)</Typography>
        <Code>{`Owner can delete any club. Admin can delete any empty club.
Club owner/admin can delete only if empty. Deletion archives name into related polls and sets club to Public.`}</Code>
        <Typography variant="subtitle1">GET {base}/clubs/:id/members/public (Auth)</Typography>
        <Code>{`Returns names of members for display to club members (or platform owner).
Response: { members: [{ userId, username, displayName, role, platformRole }] }`}</Code>
        <Typography variant="subtitle1">GET {base}/clubs/:id/members (Admin/Owner/Club Creator)</Typography>
        <Code>{`Response: { members: [{ userId, username, displayName, email, platformRole, role }] }
Notes: List club members with full details. Only club creators or platform admins can access.`}</Code>
        <Typography variant="subtitle1">POST {base}/clubs/:id/members (Admin/Owner/Club Creator)</Typography>
        <Code>{`Body: { username: string, role?: 'member' }
Response: { userId: number, username: string, role: string }
Notes: Add a member to the club by username. Role defaults to 'member'. Only club creators can add members.`}</Code>
        <Typography variant="subtitle1">DELETE {base}/clubs/:id/members/:userId (Admin/Owner/Club Creator)</Typography>
        <Code>{`Response: { message: string }
Notes: Remove a member from the club. Cannot remove club owner. Only club creators can remove members.`}</Code>
        <Typography variant="subtitle1">GET {base}/clubs/:id/webhook (Auth)</Typography>
        <Typography variant="subtitle1">PUT {base}/clubs/:id/webhook (Auth)</Typography>
        <Code>{`Manage Discord alert webhook URL and default message (club creator or platform Owner).`}</Code>
      </CardSection>

      <CardSection title="Comments (Club Discussions)" subtitle="Talk about the winner post-viewing" badges={["Auth"]}>
        <Typography variant="subtitle1">GET {base}/comments/:clubId/:pollId (Auth)</Typography>
        <Code>Owner can view any; members can view their club. Returns comments with user, per-user rating for this poll, and per-user aggregate average/count.</Code>
        <Typography variant="subtitle1">POST {base}/comments (Auth)</Typography>
        <Code>{`Body: { clubId, pollId, message }
Validation: After poll end + runtime. Owner or club members can post.`}</Code>
        <Typography variant="subtitle1">GET {base}/comments/me (Auth)</Typography>
        <Code>{`Returns your comments across clubs.
Response: { comments: [{ id, message, createdAt, club, poll }] }`}</Code>
      </CardSection>

      <CardSection title="Winners & Previously Watched" subtitle="Historical outcomes" badges={["Public"]}>
        <Typography variant="subtitle1">GET {base}/winners/summary</Typography>
        <Code>Summarized list of winning movies with rating averages and counts.</Code>
      </CardSection>

      <CardSection title="Global Metrics" subtitle="Platform-wide insights" badges={["Public","Auth"]}>
        <Typography variant="subtitle1">GET {base}/metrics/top-creators</Typography>
        <Code>{`Query: { limit?: number (default 10) }
Response: { topCreators: [{ userId, username, totalPolls }] }`}</Code>

        <Typography variant="subtitle1">GET {base}/metrics/top-winning-films</Typography>
        <Code>{`Query: { limit?: number (default 5), minRatings?: number (default 3) }
Response: { topWinningFilms: [{ movieId, title, year, poster, imdbID, ratingAvg, ratingCount, timesWon, pollIds: string[] }] }
Notes:
- Aggregates ratings for winning movies across polls; filters by minRatings.
- Sorting: ratingAvg desc, then ratingCount desc, then timesWon desc.`}</Code>

        <Typography variant="subtitle1">GET {base}/metrics/top-genres</Typography>
        <Code>{`Response: { total: number, breakdown: [{ genre, count, percentage }] }
Notes:
- Computes genre counts for all winning movies using OMDb (cached).`}</Code>

        <Typography variant="subtitle1">GET {base}/metrics/weekly-winners (Host/Owner)</Typography>
        <Code>{`Query: { days?: number (default 7, max 90) }
Response: { winners: [{ pollId, pollTitle, movieId, title, poster, imdbID, wonAt, resultsPath, resultsUrl? }] }
Notes:
- Includes winners from your clubs and Public polls.
- Requires Host plan or Owner role.`}</Code>

        <Typography variant="subtitle1">GET {base}/metrics/polls-by-user</Typography>
        <Code>{`Query: { userId?: number (defaults to current user if authed), granularity?: 'day'|'week' (default 'day') }
Response: { userId: number|null, series: [{ bucket, count, cumulative }] }
Notes:
- Use this series to render a running line graph of polls created.
- With no userId and no auth, aggregates across all polls.
- Personal series (userId provided) requires Host plan or Owner; otherwise 401/403.`}</Code>
      </CardSection>

      <CardSection title="Analytics & Attribution" subtitle="Referral capture and reporting" badges={["Public","Auth","Admin/Owner"]}>
        <Typography variant="subtitle1">POST {base}/analytics/visit (Auth)</Typography>
        <Code>{`Body: { referrer?, utmSource?, utmMedium?, utmCampaign?, utmContent?, utmTerm? }
Behavior: Attaches first-touch referral fields to the authenticated user if empty. No-op if already set.`}</Code>
        <Typography variant="subtitle1">POST {base}/analytics/event (Public/Auth)</Typography>
        <Code>{`Body: { sessionId (string), referrer?, utmSource?, utmMedium?, utmCampaign?, utmContent?, utmTerm?, landingUrl? }
Behavior: Records an anonymous attribution event with IP and User-Agent; associates to userId if token provided.
Notes: Intended for first-visit capture. sessionId should persist in localStorage.`}</Code>
        <Typography variant="subtitle1">GET {base}/analytics/report?days=30 (Admin/Owner)</Typography>
        <Code>{`Response: { days, totals: { visits, attributed }, rows: [{ utmSource, utmCampaign, visits, attributed }] }
Notes: Aggregates events over the window; attributed counts are based on subscription webhook matches.`}</Code>
      </CardSection>

      <CardSection title="Plex" subtitle="Optional integration for local libraries" badges={["Owner"]}>
        <Typography variant="subtitle1">POST {base}/plex/config (Owner)</Typography>
        <Typography variant="subtitle1">GET {base}/plex/status (Owner)</Typography>
        <Typography variant="subtitle1">GET {base}/plex/search</Typography>
        <Typography variant="subtitle1">GET {base}/plex/movies (Owner/Admin)</Typography>
        <Typography variant="subtitle1">GET {base}/plex/image?path=... (Public; proxied)</Typography>
        <Typography variant="subtitle1">POST {base}/plex/oauth/pin (Owner) • GET {base}/plex/oauth/pin/:id (Owner)</Typography>
        <Code>Notes: Movies response includes basic metadata plus OMDb enrichment (genres, IMDb rating/votes) and the extracted imdbID when available. Thumbnails are served via the image proxy with caching.
If Plex token exists but baseUrl/sections cannot be discovered (e.g., after switching DB), status returns connected=false so the UI can reconnect.</Code>
      </CardSection>

      <CardSection title="Billing (Stripe)" subtitle="Subscriptions for Host plan" badges={["Auth"]}>
        <Typography variant="subtitle1">POST {base}/billing/checkout (Auth)</Typography>
        <Code>Creates a Checkout session for the Host plan. Webhooks manage plan status.</Code>
        <Typography variant="subtitle1">POST {base}/billing/add-slots (Auth)</Typography>
        <Code>{`Body: { quantity: 1|2|3 }
Behavior: Creates a Checkout for non-stacking tiered add-ons (1: 6–9, 2: 10–19, 3: 20–29). Requires active Host plan (includes 5 clubs).
Downgrades are blocked if your owned club count exceeds the selected tier limit.
`}</Code>
        <Typography variant="subtitle1">POST {base}/billing/cancel (Auth)</Typography>
        <Code>Sets the current subscription to cancel at period end. Clubs remain but new polls are locked until you resubscribe.</Code>
        <Typography variant="subtitle1">PUT {base}/users/:id/subscription (Owner)</Typography>
        <Code>{`Body: { plan?: 'free'|'host'|'pro'|null, planStatus?: 'none'|'active'|'past_due'|'canceled'|null, clubSlotsTier?: 0|1|2|3, note?: string }
Behavior: Normalizes null/empty to model defaults (free/none). Logs to UserSubscriptionAudits.
Tier rules: Host includes 5 clubs (tier 0). Tier 1 = 6–9, Tier 2 = 10–19, Tier 3 = 20–29. Downgrades are blocked if owned club count exceeds the target tier.`}</Code>
      </CardSection>

      <CardSection title="Users (Admin/Owner)" subtitle="Manage platform users" badges={["Admin/Owner"]}>
        <Typography variant="subtitle1">GET {base}/users</Typography>
        <Code>{`Returns list of users (id, username, email, role, plan, planStatus, clubSlotsTier, createdAt, updatedAt).`}</Code>
        <Typography variant="subtitle1">PUT {base}/users/:id (Admin/Owner)</Typography>
        <Code>{`Body: { email?, password?, role? }
Notes:
- Only Owners can assign 'owner' role.
- Admins can switch between 'admin' and 'user'.`}</Code>
      </CardSection>

      <CardSection title="Club Streamer" subtitle="YouTube streaming with chat and playlists" badges={["Auth","Club Member"]}>
        <Typography variant="subtitle1">GET {base}/club-streamer/:clubId/chat (Auth)</Typography>
        <Code>{`Query: { limit?: number (default 50), offset?: number (default 0) }
Response: { messages: [{ id, message, createdAt, user: { id, username, displayName } }] }
Notes: Returns chat messages for the club. Only club members can access.`}</Code>
        <Typography variant="subtitle1">POST {base}/club-streamer/:clubId/chat (Auth)</Typography>
        <Code>{`Body: { message: string (1-1000 chars) }
Response: { message: { id, message, createdAt, user: { id, username, displayName } } }
Error 429: { error: string, spamScore: number, reasons: string[] }
Notes: Send a chat message. Only club members can send messages. Includes spam detection with rate limiting (10 messages/60s), similarity checking, and pattern detection.`}</Code>
        <Typography variant="subtitle1">DELETE {base}/club-streamer/:clubId/chat/:messageId (Auth)</Typography>
        <Code>{`Response: { message: string }
Notes: Delete a chat message. Only admins, owners, or the message author can delete messages.`}</Code>
        <Typography variant="subtitle1">GET {base}/club-streamer/:clubId/playlist (Auth)</Typography>
        <Code>{`Response: { playlist: [{ id, title, videoId, platform, thumbnail, duration, position, isPlaying, isCompleted, addedBy: { id, username, displayName } }] }
Notes: Get the club's video playlist. Only club members can access.`}</Code>
        <Typography variant="subtitle1">POST {base}/club-streamer/:clubId/playlist (Auth)</Typography>
        <Code>{`Body: { title: string, videoId: string, platform: 'youtube'|'twitch'|'kick', thumbnail?: string, duration?: string }
Response: { item: { id, title, videoId, platform, thumbnail, duration, position, addedBy: { id, username, displayName } } }
Notes: Add a video to the playlist. Supports YouTube, Twitch TV, and Kick. Only club members can add videos.`}</Code>
        <Typography variant="subtitle1">DELETE {base}/club-streamer/:clubId/playlist/:itemId (Auth)</Typography>
        <Code>{`Response: { message: string }
Notes: Remove a video from the playlist. Only the video adder, admins, or owners can remove videos.`}</Code>
        <Typography variant="subtitle1">PUT {base}/club-streamer/:clubId/playlist/:itemId/position (Auth)</Typography>
        <Code>{`Body: { position: number }
Response: { message: string }
Notes: Reorder playlist items. Only club members can reorder.`}</Code>
        <Typography variant="subtitle1">PUT {base}/club-streamer/:clubId/playlist/:itemId/playing (Auth)</Typography>
        <Code>{`Response: { message: string }
Notes: Mark a video as currently playing. Only club members can control playback.`}</Code>
        <Typography variant="subtitle1">PUT {base}/club-streamer/:clubId/playlist/:itemId/completed (Auth)</Typography>
        <Code>{`Response: { message: string }
Notes: Mark a video as completed. Only club members can mark videos as completed.`}</Code>
      </CardSection>

      <CardSection title="Physical Media Collection" subtitle="Manage your movie collection" badges={["Auth","Owner"]}>
        <Typography variant="subtitle1">GET {base}/physical-media (Auth)</Typography>
        <Code>{`Query: { page?: number, limit?: number, search?: string, format?: string, year?: number }
Response: { items: PhysicalMedia[], totalPages, currentPage, total }
Notes: Returns user's physical media collection with pagination and filtering. Search is case-insensitive and database-agnostic.`}</Code>
        <Typography variant="subtitle1">GET {base}/physical-media/:id (Auth)</Typography>
        <Code>{`Response: { id, title, year, genre, director, plot, runtime, rating, format, barcode, datePurchased, purchasePrice, locationPurchased, isWatched, watchDate, posterUrl, backdropUrl, imdbId, cast, distributor, edition, audioTracks, subtitles, regions, packaging, notes, createdAt, updatedAt }
Notes: Get detailed information for a specific physical media item.`}</Code>
        <Typography variant="subtitle1">POST {base}/physical-media (Auth)</Typography>
        <Code>{`Body: { title, year, genre?, director?, plot?, runtime?, rating?, format?, barcode?, datePurchased?, purchasePrice?, locationPurchased?, isWatched?, watchDate?, posterUrl?, backdropUrl?, imdbId?, cast?, distributor?, edition?, audioTracks?, subtitles?, regions?, packaging?, notes? }
Response: { id, ... }
Notes: Create a new physical media item. All fields except title and year are optional.`}</Code>
        <Typography variant="subtitle1">PUT {base}/physical-media/:id (Auth)</Typography>
        <Code>{`Body: { title?, year?, genre?, director?, plot?, runtime?, rating?, format?, barcode?, datePurchased?, purchasePrice?, locationPurchased?, isWatched?, watchDate?, posterUrl?, backdropUrl?, imdbId?, cast?, distributor?, edition?, audioTracks?, subtitles?, regions?, packaging?, notes? }
Response: { id, ... }
Notes: Update an existing physical media item. Only provided fields will be updated.`}</Code>
        <Typography variant="subtitle1">DELETE {base}/physical-media/:id (Auth)</Typography>
        <Code>{`Response: { message: string }
Notes: Delete a physical media item. Only the owner can delete their items.`}</Code>
        <Typography variant="subtitle1">POST {base}/physical-media/migrate-from-clz-xml (Auth)</Typography>
        <Code>{`Body: { preview?: boolean (default false), limit?: number (default 0 for all), chunkSize?: number (default 50) }
Response: { movies: MovieData[], summary: { total, new, willUpdate, noUpdatesNeeded, duplicatesSkipped, withImages, withPurchaseInfo, watched }, fieldChanges?: FieldChange[] }
Notes: Import movies from XML export. Preview mode shows what would be imported without saving. Supports chunked processing for large collections.`}</Code>
        <Typography variant="subtitle1">GET {base}/physical-media/search?q=... (Auth)</Typography>
        <Code>{`Query: { q: string, format?: string, year?: number }
Response: { items: PhysicalMedia[] }
Notes: Search physical media collection for poll creation. Returns items matching the search query.`}</Code>
      </CardSection>

      <CardSection title="Admin" subtitle="Platform Owner settings" badges={["Owner"]}>
        <Typography variant="subtitle1">GET {base}/admin/db (Owner)</Typography>
        <Typography variant="subtitle1">PUT {base}/admin/db (Owner)</Typography>
        <Code>Switch between SQLite and Postgres. PUT writes env vars and clears cache; restart required. Cache keys are namespaced per DB to prevent data leakage.</Code>
        <Typography variant="subtitle1">GET {base}/admin/db/export (Owner)</Typography>
        <Typography variant="subtitle1">POST {base}/admin/db/import (Owner)</Typography>
        <Code>{`Export the database as JSON and import it later. Import supports replace or upsert modes.
Payload for import: { data: { ModelName: [rows...] }, mode?: 'replace'|'upsert' }`}</Code>
        <Typography variant="subtitle1">POST {base}/admin/gdpr/wipe/:userId (Admin/Owner)</Typography>
        <Code>{`Behavior: Deletes the user's comments, Club Streamer chat messages, anonymizes votes (userId=null), reassigns clubs and created polls to the platform Owner, deletes referral events, and removes the user account.
Response: { message }
Notes: Cannot wipe the Owner account. Use with caution.`}</Code>
        <Typography variant="subtitle1">GET {base}/admin/chat/stats (Admin/Owner)</Typography>
        <Code>{`Response: { stats: { total_messages, active_clubs, avg_message_length, max_message_length, messages_last_week, messages_last_day } }
Notes: Get Club Streamer chat statistics for monitoring database growth.`}</Code>
        <Typography variant="subtitle1">POST {base}/admin/chat/cleanup (Admin/Owner)</Typography>
        <Code>{`Response: { message: string, result: { oldMessagesDeleted, countLimitDeleted, compressedCount, statsBefore, statsAfter } }
Notes: Manually trigger comprehensive chat cleanup including compression and count limits.`}</Code>
        <Typography variant="subtitle1">GET {base}/admin/spam/stats (Admin/Owner)</Typography>
        <Code>{`Response: { stats: { totalSpamScore, flaggedUsers, threshold, rateLimitWindow, maxMessagesPerWindow } }
Notes: Get spam detection statistics and system configuration.`}</Code>
        <Typography variant="subtitle1">GET {base}/admin/spam/user/:userId (Admin/Owner)</Typography>
        <Code>{`Response: { userId: number, spamScore: number }
Notes: Get a specific user's spam score for monitoring.`}</Code>
        <Typography variant="subtitle1">POST {base}/admin/spam/user/:userId/reset (Admin/Owner)</Typography>
        <Code>{`Response: { message: string, userId: number }
Notes: Reset a user's spam score to 0. Use for false positives or after user behavior improves.`}</Code>
      </CardSection>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">Note: Some endpoints are role‑restricted. Pass the JWT as: Authorization: Bearer &lt;token&gt;.</Typography>
    </Box>
  );
};

export default ApiDocs;


